import { type ChangeEvent, type MouseEvent } from 'react';
import { ReactComponent as RadioUncheckedIcon } from '../../../assets/shared/ic-radio-unchecked.svg';
import { ReactComponent as RadioCheckedIcon } from '../../../assets/shared/ic-radio-checked.svg';
import { ReactComponent as RadioCheckedDotIcon } from '../../../assets/shared/ic-radio-checked-dot.svg';
import { useFontClass, Text } from '../../tokens';
import {
  type LiliRadioInputProps,
  LiliRadioInputSize,
  LiliRadioInputVariant,
} from './LiliRadioInput.types';

export { LiliRadioInputSize, LiliRadioInputVariant } from './LiliRadioInput.types';
export type { LiliRadioInputProps } from './LiliRadioInput.types';

export const LiliRadioInput = (props: LiliRadioInputProps) => {
  const {
    checked = false,
    disabled = false,
    size = LiliRadioInputSize.MEDIUM,
    label,
    id,
    className,
    variant = LiliRadioInputVariant.CHECKMARK,
    uncheckedHoverClassName = 'group-hover:text-outline-brand-primary',
    stopClickPropagation = false,
    ...inputProps
  } = props;

  const { fontClass } = useFontClass();
  const getSizeClass = () => {
    switch (size) {
      case LiliRadioInputSize.SMALL:
        return 'w-[16px] h-[16px]';
      case LiliRadioInputSize.LARGE:
        return 'w-[28px] h-[28px]';
      case LiliRadioInputSize.MEDIUM:
      default:
        return 'w-[18px] h-[18px]';
    }
  };

  const sizeClass = getSizeClass();

  const handleClick = (event: MouseEvent) => {
    if (stopClickPropagation) event.stopPropagation();
    if (disabled) return;
    inputProps.onChange?.({
      target: { checked: !checked },
    } as ChangeEvent<HTMLInputElement>);
  };

  const getCheckedIcon = () => {
    const baseClass = `w-full h-full ${disabled ? 'text-outline-primary-bw-04' : 'text-surface-brand-action-03'}`;
    switch (variant) {
      case LiliRadioInputVariant.DOT:
        return <RadioCheckedDotIcon className={baseClass} />;
      case LiliRadioInputVariant.CHECKMARK:
      default:
        return <RadioCheckedIcon className={baseClass} />;
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative inline-flex items-center gap-[8px] ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className || ''}`}>
      <input
        id={id}
        type='radio'
        disabled={disabled}
        checked={checked}
        className='sr-only'
        {...inputProps}
      />
      <span className={`shrink-0 transition-colors duration-200 ease-in ${sizeClass}`}>
        {checked ? (
          getCheckedIcon()
        ) : (
          <RadioUncheckedIcon
            className={`w-full h-full ${disabled ? 'text-outline-primary-bw-04' : 'text-outline-primary-bw-06'} ${!disabled && uncheckedHoverClassName}`}
          />
        )}
      </span>
      {label && (
        <label htmlFor={id}>
          <Text.H9 className={`${fontClass} ${disabled && 'text-text-main-tertiary'}`}>
            {label}
          </Text.H9>
        </label>
      )}
    </div>
  );
};
